import { Badge, Group, Paper, Skeleton, Stack, Text } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { useLatestSiloReading } from '../api';
import dayjs from '../dayjs';
import type { LocationDto } from '../types';
import { formatTonnes } from '../utils';
import { VarianceBadge } from './VarianceBadge';

/** One tile in the silo status overview (BUILD_SPEC.md §11 home strip pattern,
 * §8.2's own overview requirement: "each silo's latest reading / estimated
 * level"). Each card independently fetches its own latest reading rather than the
 * page batching one giant request — silo counts per site are small (a handful),
 * so N small cached queries are simpler than inventing a batch endpoint this goal
 * does not own (that would be a `reporting` module concern — G19).
 *
 * Routes through a bare `Link` (not Mantine's `component={Link}` polymorphism)
 * because TanStack Router's `search` prop type is inferred from the literal `to`
 * string via conditional generics — Mantine's polymorphic `component` prop types
 * the merged props generically and loses that inference, so `search` fails to
 * typecheck when routed through it. `Link` wraps the styled `Paper` instead. */
export function SiloStatusCard({ location }: { location: LocationDto }) {
  const { data, isLoading } = useLatestSiloReading(location.id);
  const latest = data?.items[0];

  return (
    <Link
      to="/silo-counts/new-reading"
      search={{ locationId: location.id }}
      style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
    >
      <Paper withBorder radius="md" p="md">
        <Stack gap={6}>
          <Group justify="space-between" wrap="nowrap">
            <Text fw={600}>{location.name}</Text>
            <Badge variant="outline" size="sm">
              {location.code}
            </Badge>
          </Group>
          {isLoading ? (
            <Skeleton height={20} width="70%" />
          ) : latest ? (
            <>
              <Group justify="space-between" wrap="nowrap">
                <Text size="xl" fw={700}>
                  {formatTonnes(latest.estMassKg)}
                </Text>
                <VarianceBadge varianceKg={latest.varianceKg} variancePct={latest.variancePct} />
              </Group>
              <Text size="xs" c="dimmed">
                Level {latest.levelValue}
                {latest.levelUnit === 'PCT' ? '%' : 'm'} · read {dayjs(latest.readAt).fromNow()}
              </Text>
            </>
          ) : (
            <Text size="sm" c="dimmed">
              No readings yet — tap to take one.
            </Text>
          )}
        </Stack>
      </Paper>
    </Link>
  );
}
